import { Sha256 } from "@aws-crypto/sha256-js";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { getCloudConfig } from "./cloudConfig";
import { cloudAuthSession } from "./cloudAuthSession";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const extractErrorMessage = async (response: Response): Promise<string> => {
  const text = await response.text();
  if (!text.trim()) {
    return `${response.status} ${response.statusText}`;
  }

  try {
    const parsed = JSON.parse(text) as unknown;
    if (isRecord(parsed) && typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // Keep original text when body is not JSON.
  }

  return text;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text.trim()) {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
};

const withQuery = (
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
): string => {
  if (!query) {
    return path;
  }

  const url = new URL(path, "https://placeholder.local");
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) {
      continue;
    }
    url.searchParams.set(k, String(v));
  }

  return `${url.pathname}${url.search}`;
};

const request = async <T>(
  method: "GET" | "PUT" | "DELETE" | "POST",
  path: string,
  options?: {
    body?: unknown;
    query?: Record<string, string | number | boolean | undefined>;
  },
): Promise<T> => {
  const { apiGatewayBaseUrl, executeApiRegion } = getCloudConfig();
  const credentials = await cloudAuthSession.getAwsCredentials();
  const url = new URL(withQuery(path, options?.query), apiGatewayBaseUrl);

  const hasBody = options?.body !== undefined;
  const body = hasBody ? JSON.stringify(options.body) : undefined;

  const signer = new SignatureV4({
    credentials,
    region: executeApiRegion,
    service: "execute-api",
    sha256: Sha256,
  });


  const headers: Record<string, string> = { host: url.host };
  if (hasBody) {
    headers["content-type"] = "application/json";
  }

  const query: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    query[key] = value;
  });

  const signed = await signer.sign(
    new HttpRequest({
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port ? Number(url.port) : undefined,
      method,
      path: url.pathname,
      query,
      headers,
      body,
    }),
  );

  const fetchHeaders = new Headers(signed.headers as Record<string, string>);
  fetchHeaders.delete("host");

  const response = await fetch(url.toString(), {
    method,
    headers: fetchHeaders,
    body,
  });

  if (!response.ok) {
    const errorMessage = await extractErrorMessage(response);
    throw new Error(errorMessage);
  }

  return parseResponse<T>(response);
};

const unwrapObject = <T>(payload: unknown): T => {
  if (isRecord(payload)) {
    if ("data" in payload) {
      return payload.data as T;
    }
    if ("item" in payload) {
      return payload.item as T;
    }
  }
  return payload as T;
};

const unwrapArray = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (isRecord(payload)) {
    if (Array.isArray(payload.items)) {
      return payload.items as T[];
    }
    if (Array.isArray(payload.data)) {
      return payload.data as T[];
    }
  }

  return [];
};

export const sigv4ApiClient = {
  list: async <T>(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
  ): Promise<T[]> => {
    const payload = await request<unknown>("GET", path, { query });
    return unwrapArray<T>(payload);
  },

  get: async <T>(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
  ): Promise<T | undefined> => {
    const payload = await request<unknown>("GET", path, { query });
    if (payload === undefined || payload === null) {
      return undefined;
    }
    return unwrapObject<T>(payload);
  },

  put: async <T>(path: string, body: unknown): Promise<T> => {
    const payload = await request<unknown>("PUT", path, { body });
    return unwrapObject<T>(payload);
  },

  post: async <T>(path: string, body: unknown): Promise<T> => {
    const payload = await request<unknown>("POST", path, { body });
    return unwrapObject<T>(payload);
  },

  delete: async (path: string, query?: Record<string, string | number | boolean | undefined>): Promise<boolean> => {
    await request<unknown>("DELETE", path, { query });
    return true;
  },

  getRaw: request,
};
